<template>
  <div class="w-100 position-relative">

    <p class="mb-0 pb-0" style="margin-left:2px;">{{ label }}</p>

    <div v-if="maxExceeded" class="text-danger mb-2">
      Wybrano maksymalną liczbę ({{ max }}).
    </div>

    <!-- Tag list with input -->
    <div
        class="form-control d-flex flex-wrap gap-1"
        @click="focusInput"
        style="min-height: 36px; cursor: text;"
    >
      <!-- Selected tags -->
      <span
          v-for="item in internalValue"
          :key="item[valueKey]"
          class="d-flex align-items-center bg-primary text-white border-0 rounded px-2 py-1 mb-1 w-100"
          style="font-size: 12px;"
      >
        {{ item[labelKey] }}
        <button
            @click.stop="removeItem(item)"
            class="ms-1 text-white border-0 rounded bg-primary ms-auto"
            aria-label="Usuń tag"
        >
          &times;
        </button>
      </span>

      <!-- Search input -->
      <div class="d-flex align-items-center flex-grow-1" style="min-width: 100px;">
        <input
            ref="inputRef"
            v-model="search"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="selectHighlighted"
            @focus="open = true"
            @blur="closeWithDelay"
            class="border-0 form-control p-0 m-0"
            style="min-width: 100px;"
            placeholder="Wyszukaj..."
        />

        <!-- Spinner po prawej stronie -->
        <div v-if="loading" class="ms-2 d-flex align-items-center">
          <div class="spinner-border spinner-border-sm text-secondary" role="status">
            <span class="visually-hidden">Ładowanie...</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Dropdown results -->
    <ul
        v-if="open && results.length"
        class="dropdown-menu show shadow-sm position-absolute mt-1"
        style="max-height: 240px; max-width: 500px; overflow-y: auto; z-index: 1050;"
    >
      <li
          v-for="(r, idx) in results"
          :key="r[valueKey]"
          @mousedown.prevent="toggle(r)"
          class="dropdown-item py-1 px-2 small"
          :class="{ 'active': idx === highlighted }"
          @mouseover="highlighted = idx"
      >
        {{ r[labelKey] }}
      </li>
    </ul>

  </div>
</template>

<script>
import {ref, watch, onMounted} from 'vue'

export default {
  name: "TagSelect",

  props: {
    modelValue: {type: [Array, String], default: () => []},
    endpoint: {type: String, required: true},
    label: {type: String},
    labelKey: {type: String, default: 'name'},
    valueKey: {type: String, default: 'id'},
    max: {type: Number, default: Infinity},
    debounce: {type: Number, default: 300},
  },

  emits: ['update:modelValue'],

  setup(props, {emit}) {
    const search = ref('')
    const results = ref([])
    const open = ref(false)
    const highlighted = ref(-1)
    const internalValue = ref([])
    const loading = ref(false)
    const maxExceeded = ref(false)
    const inputRef = ref(null)

    let timer = null

    // Obsługa initial value
    onMounted(() => {
      if (typeof props.modelValue === 'string' && props.modelValue.length > 0) {
        internalValue.value = props.modelValue.split(',').map(v => ({
          [props.labelKey]: v.trim(),
          [props.valueKey]: v.trim()
        }))
      }
    })

    // Synchronizacja z v-model
    watch(() => props.modelValue, (val) => {
      if (typeof val === 'string' && val.length > 0) {
        internalValue.value = val.split(',').map(v => ({
          [props.labelKey]: v.trim(),
          [props.valueKey]: v.trim()
        }))
      } else {
        internalValue.value = []
      }
    }, {immediate: true})

    // Debounced search
    watch(search, val => {
      if (!val.trim()) {
        results.value = []
        return
      }
      clearTimeout(timer)
      timer = setTimeout(fetchResults, props.debounce)
    })

    async function fetchResults() {
      loading.value = true
      try {
        const res = await fetch(`${props.endpoint}?q=${encodeURIComponent(search.value)}`)
        if (!res.ok) throw new Error('Fetch error')
        results.value = await res.json()
        highlighted.value = 0
      } catch (e) {
        console.error(e)
        results.value = []
      } finally {
        loading.value = false
      }
    }

    function toggle(item) {
      if (internalValue.value.length >= props.max) {
        maxExceeded.value = true
        return
      }
      maxExceeded.value = false

      const idx = internalValue.value.findIndex(i => i[props.valueKey] === item[props.valueKey])
      if (idx === -1) {
        internalValue.value.push(item)
      } else {
        internalValue.value.splice(idx, 1)
      }

      emitValue()
      search.value = ''
      results.value = []
    }

    function removeItem(item) {
      internalValue.value = internalValue.value.filter(i => i[props.valueKey] !== item[props.valueKey])
      maxExceeded.value = false
      emitValue()
    }

    function emitValue() {
      // zwraca string po przecinku np: "email1,email2,email3"
      const values = internalValue.value.map(item => item[props.valueKey]);
      emit('update:modelValue', values.join(','));
    }

    function move(delta) {
      if (!results.value.length) return
      highlighted.value = (highlighted.value + delta + results.value.length) % results.value.length
    }

    function selectHighlighted() {
      if (highlighted.value >= 0 && highlighted.value < results.value.length) {
        toggle(results.value[highlighted.value])
      } else if (search.value.trim()) {
        const customValue = search.value.trim()
        const isEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(customValue)
        if (!isEmail) return

        const alreadyExists = internalValue.value.some(i => i[props.valueKey] === customValue)
        if (!alreadyExists && internalValue.value.length < props.max) {
          maxExceeded.value = false
          internalValue.value.push({
            [props.labelKey]: customValue,
            [props.valueKey]: customValue
          })
          emitValue()
        }

        search.value = ''
        results.value = []
      }
    }

    function closeWithDelay() {
      setTimeout(() => (open.value = false), 150)
    }

    function focusInput() {
      inputRef.value?.focus()
    }

    return {
      search, results, open, highlighted, internalValue, loading, maxExceeded, inputRef,
      toggle, removeItem, move, selectHighlighted, closeWithDelay, focusInput
    }
  }
}
</script>


<style scoped>
.dropdown-item.active {
  background-color: #f0f0f0 !important;
  color: inherit;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
  cursor: pointer;
}
</style>
